import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Send, Edit, Trash2, FileText, Clock, DollarSign, X, Loader2, CheckCircle } from 'lucide-react';

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendName, setSendName] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [id]);

  const fetchContract = async () => {
    try {
      const res = await api.get(`/contracts/${id}`);
      const contractData = res.data.contract;
      const signaturesData = res.data.signatures || [];
      
      setContract(contractData);
      setSignatures(signaturesData);
      
      // Fetch client info if contract has client_id
      if (contractData.client_id) {
        try {
          const clientRes = await api.get(`/clients/${contractData.client_id}`);
          setClientInfo(clientRes.data);
        } catch (clientError) {
          console.error('Failed to load client info:', clientError);
        }
      }
    } catch (error) {
      console.error('Failed to load contract:', error);
      toast.error('Failed to load contract');
      navigate('/contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleSendForSignature = () => {
    // Prefill with client info if available
    if (clientInfo) {
      setSendName(clientInfo.contact_name || clientInfo.company_name || '');
      setSendEmail(clientInfo.email || clientInfo.contact_email || '');
    } else {
      setSendName('');
      setSendEmail('');
    }
    setShowSendDialog(true);
  };

  const handleSendConfirm = async () => {
    if (!sendEmail || !sendEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (!sendName || !sendName.trim()) {
      toast.error('Please enter recipient name');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sendEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      await api.post(`/contracts/${id}/send-for-signature`, { 
        recipient_email: sendEmail,
        recipient_name: sendName
      });
      toast.success(`Contract sent to ${sendEmail}`);
      setShowSendDialog(false);
      setSendEmail('');
      setSendName('');
      fetchContract(); // Refresh to update status
    } catch (error) {
      console.error('Failed to send contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to send contract');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this contract?')) {
      return;
    }

    try {
      await api.delete(`/contracts/${id}`);
      toast.success('Contract deleted successfully');
      navigate('/contracts');
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      // Fetch the HTML content
      const response = await api.get(`/contracts/${id}/pdf`);
      
      // Open a new window and write the HTML content directly
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(response.data);
        printWindow.document.close();
        
        toast.success('Opening print preview...');
      } else {
        toast.error('Please allow pop-ups to print the contract');
      }
    } catch (error) {
      console.error('Failed to open print preview:', error);
      toast.error('Failed to open print preview');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'pending_signature':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'signed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || !contract) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/contracts')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contracts
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{contract.title}</h1>
            <p className="text-slate-500 mt-1">Contract ID: {contract.id}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(contract?.status || 'draft')}`}>
            {formatStatus(contract?.status || 'draft')}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <Button
          onClick={() => navigate(`/contracts/${id}/edit`)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </Button>
        <Button
          onClick={handleDownloadPDF}
          variant="outline"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        {contract.requires_signature && contract.status === 'draft' && (
          <Button
            onClick={handleSendForSignature}
            variant="outline"
            className="border-green-300 text-green-600 hover:bg-green-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Send for Signature
          </Button>
        )}
        {contract.requires_signature && (contract.status === 'sent' || contract.status === 'signed' || contract.status === 'pending_signature') && (
          <Button
            onClick={handleSendForSignature}
            variant="outline"
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Resend Email
          </Button>
        )}
        <Button
          onClick={handleDelete}
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50 ml-auto"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Contract Content
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: contract.content }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-500">Type</label>
                <p className="text-slate-900 capitalize">{contract.document_type || 'Contract'}</p>
              </div>

              {contract.client_id && clientInfo && (
                <div>
                  <label className="text-sm font-medium text-slate-500">Client</label>
                  <p className="text-slate-900">{clientInfo.company_name || 'Unknown'}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Start Date
                </label>
                <p className="text-slate-900">{formatDate(contract.start_date)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  End Date
                </label>
                <p className="text-slate-900">{formatDate(contract.end_date)}</p>
              </div>

              {contract.value && (
                <div>
                  <label className="text-sm font-medium text-slate-500 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    Contract Value
                  </label>
                  <p className="text-slate-900 font-semibold">
                    {contract.currency || 'USD'} {contract.value.toLocaleString()}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-500">Created</label>
                <p className="text-slate-900">{formatDate(contract.created_at)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Last Updated</label>
                <p className="text-slate-900">{formatDate(contract.updated_at)}</p>
              </div>

              {contract.requires_signature && (
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${contract.status === 'signed' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                    <span className="text-slate-600">
                      {contract.status === 'signed' ? 'Signature received' : 'Signature required'}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Information Card */}
          {clientInfo && (
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-base">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Company</label>
                  <p className="text-sm text-slate-900 font-medium">{clientInfo.company_name}</p>
                </div>
                {clientInfo.contact_name && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Contact</label>
                    <p className="text-sm text-slate-900">{clientInfo.contact_name}</p>
                  </div>
                )}
                {(clientInfo.email || clientInfo.contact_email) && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Email</label>
                    <p className="text-sm text-slate-900">{clientInfo.email || clientInfo.contact_email}</p>
                  </div>
                )}
                {clientInfo.phone && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Phone</label>
                    <p className="text-sm text-slate-900">{clientInfo.phone}</p>
                  </div>
                )}
                {clientInfo.address && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Address</label>
                    <p className="text-sm text-slate-900">{clientInfo.address}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Signature Details Card */}
          {contract.status === 'signed' && signatures.length > 0 && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Signature Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {signatures.map((sig, index) => (
                  <div key={index} className={index > 0 ? 'pt-3 border-t border-green-200' : ''}>
                    {/* Signature Image */}
                    {sig.signature_data && sig.signature_type === 'drawn' && (
                      <div>
                        <label className="text-xs font-medium text-slate-600">Signature</label>
                        <div className="mt-1 border border-slate-300 rounded p-2 bg-white">
                          <img 
                            src={sig.signature_data}
                            alt="Signature"
                            className="max-w-full h-auto"
                            style={{ maxHeight: '100px' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Typed Signature */}
                    {sig.signature_data && sig.signature_type === 'typed' && (
                      <div>
                        <label className="text-xs font-medium text-slate-600">Signature</label>
                        <div className="mt-1 border border-slate-300 rounded p-2 bg-white">
                          <p className="text-2xl font-cursive" style={{ fontFamily: 'cursive' }}>
                            {sig.signature_data}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Signer Name */}
                    {sig.signer_name && (
                      <div>
                        <label className="text-xs font-medium text-slate-600">Signed By</label>
                        <p className="text-sm text-slate-900">{sig.signer_name}</p>
                      </div>
                    )}

                    {/* Signer Email */}
                    {sig.signer_email && (
                      <div>
                        <label className="text-xs font-medium text-slate-600">Email</label>
                        <p className="text-sm text-slate-900">{sig.signer_email}</p>
                      </div>
                    )}

                    {/* Signing Date */}
                    {sig.signed_at && (
                      <div>
                        <label className="text-xs font-medium text-slate-600">Signed On</label>
                        <p className="text-sm text-slate-900">
                          {formatDate(sig.signed_at)}
                        </p>
                      </div>
                    )}

                    {/* Security Metadata */}
                    {(sig.signer_ip || sig.user_agent || sig.device_info) && (
                      <div className="pt-2 border-t border-green-200">
                        <label className="text-xs font-medium text-slate-600 block mb-2">Security Details</label>
                        
                        {sig.signer_ip && (
                          <div className="text-xs text-slate-600 mb-1">
                            <span className="font-medium">IP Address:</span> {sig.signer_ip}
                          </div>
                        )}
                        
                        {sig.user_agent && (
                          <div className="text-xs text-slate-600 mb-1">
                            <span className="font-medium">Browser:</span> {sig.user_agent.substring(0, 50)}...
                          </div>
                        )}

                        {sig.device_info && sig.device_info.platform && (
                          <div className="text-xs text-slate-600 mb-1">
                            <span className="font-medium">Platform:</span> {sig.device_info.platform}
                          </div>
                        )}

                        {sig.device_info && sig.device_info.timestamp && (
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">Timestamp:</span> {new Date(sig.device_info.timestamp).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Version History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Version History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-slate-500">
                <p>Version {contract.current_version_id ? '1.1' : '1.0'}</p>
                <p className="text-xs mt-1">Last modified: {formatDate(contract.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Send Contract Dialog */}
      {showSendDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-slate-900">Send Contract for Signature</h3>
              <button
                onClick={() => setShowSendDialog(false)}
                className="text-slate-400 hover:text-slate-600"
                disabled={sending}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="sendName">Recipient Name *</Label>
                <Input
                  id="sendName"
                  type="text"
                  value={sendName}
                  onChange={(e) => setSendName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1"
                  disabled={sending}
                  autoFocus
                />
              </div>
              
              <div>
                <Label htmlFor="sendEmail">Recipient Email Address *</Label>
                <Input
                  id="sendEmail"
                  type="email"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="mt-1"
                  disabled={sending}
                />
                <p className="text-sm text-slate-500 mt-2">
                  The contract will be sent to this email address for signature.
                </p>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  onClick={handleSendConfirm}
                  disabled={sending}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Contract
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setShowSendDialog(false)}
                  variant="outline"
                  disabled={sending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetail;
